import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PageContext } from '@/context/PageContext';
import { useTranslatableField } from '@/utils/useTranslatableField';
import { determineHomepage, determineLogoRedirectUrl, determineSafeUrl, determineThemeName } from 'utils/determineUrls';
import { useScreenSize } from 'utils/useScreenSize';
import { ImageBoss } from 'components/Content/Atoms/ImageBoss';
import { resolveHeaderDisplay } from './resolveHeaderDisplay';
import styles from './Header.module.scss';

const DEFAULT_MOBILE_HEIGHT = 48;
const DEFAULT_DESKTOP_HEIGHT = 72;
const FALLBACK_LOGO_URL = '/zencity_white.svg';

/**
 * The branded logo/text block shared by every header variant, so the logo is
 * sized, linked and labelled identically on every screen.
 */
export function HeaderBrand() {
  const { theme, urlParams, slug, linkHostname } = useContext(PageContext);
  const { translateField } = useTranslatableField(urlParams?.locale);
  const { isMobileView } = useScreenSize();
  const { t } = useTranslation();

  const logoUrl = theme?.brandLogoUrl || translateField(theme?.logo, theme?.defaultLanguage) || FALLBACK_LOGO_URL;
  const { headerText, showLogo, showText } = resolveHeaderDisplay(theme);
  const organization = slug?.[0] || theme?.organization || '';
  const redirectUrl =
    determineLogoRedirectUrl(theme) || determineHomepage(organization, urlParams?.locale, linkHostname);
  const safeRedirectUrl = redirectUrl ? determineSafeUrl(redirectUrl) : null;
  const logoMaxHeight = isMobileView
    ? theme?.logoMobileMaxHeight || DEFAULT_MOBILE_HEIGHT
    : theme?.logoDesktopMaxHeight || DEFAULT_DESKTOP_HEIGHT;
  const altText = t('homeLink', { org: determineThemeName(theme, translateField) });

  return (
    <div className={styles.brand}>
      {/* Plain anchor, not next/link: this is an absolute URL that may point at the city's own
          site, and next/link normalises absolute hrefs against `trailingSlash` on the client
          only — the resulting server/client difference breaks hydration. */}
      <a href={safeRedirectUrl || '#'} className={styles.logoLink}>
        {showLogo && <ImageBoss url={logoUrl} alt={altText} height={logoMaxHeight} className={styles.logoImage} />}
        {showText && <span className={styles.headerText}>{headerText}</span>}
      </a>
    </div>
  );
}
